// combatpro/views/EncounterBriefView.js
//
// Combat PRO - Encounter Brief (Battle Setup System).
//
// A reusable battle-specific layer describing what makes THIS encounter different
// while it is being run: goal / victory / failure, tactical battle setup, structured
// events (waves, reinforcements, phase changes) and special rules (hazards, custom
// rules, monster variants). It is NOT a mission, campaign or story system.
//
// The brief is tied to the ACTIVE ENCOUNTER: it reads and writes the active slot's
// encounterBrief through CombatContext handlers, so switching the Active Encounter
// switches the brief and nothing leaks between encounters. Round / HP triggers read
// the existing live combat state (currentRound, monsters) as HINTS only; the DM keeps
// manual activation/resolution control.
//
// One activeView ('view-encounter-brief') with internal sub-page routing: an overview
// that doubles as the compact during-combat run reference, plus four detail pages
// opened from the four cards. Reuses the existing dark visual language and the shared
// vi-tr-* control primitives (chip / primary / btn / input). No optional chaining, no
// nullish coalescing, no em dash.

import React, { useContext, useState } from 'react';
import { CombatContext } from '../contexts/CombatContext';
import {
  defaultBrief, briefSummary, briefIsConfigured, evaluateEventDue, triggerLabel, nextEvent,
  EVENT_KINDS, EVENT_KIND_LABELS, TRIGGER_TYPES, TRIGGER_LABELS, EVENT_STATUSES,
  RULE_KINDS, RULE_KIND_LABELS, RESOLUTIONS, RESOLUTION_LABELS
} from '../encounter/encounterBriefEngine';
import { newZone } from '../runtime/dmOpsEngine';
import { familyArrayKey, activeFamilyOf } from '../encounter/encounterStoreEngine';

// Populated monster slots for the slot dropdowns (HP triggers, monster variants).
function populatedSlots(monsters, count) {
  const list = [];
  const arr = Array.isArray(monsters) ? monsters : [];
  const n = (typeof count === 'number' && count > 0) ? Math.min(count, arr.length) : arr.length;
  for (let i = 0; i < n; i++) {
    const m = arr[i];
    if (!m) { continue; }
    const cn = (m.customName && String(m.customName).trim()) ? String(m.customName).trim() : '';
    const sm = (m.selectedMonster && String(m.selectedMonster).trim()) ? String(m.selectedMonster).trim() : '';
    if (!cn && !sm) { continue; }
    list.push({ index: i, label: (cn || sm) + ' (slot ' + (i + 1) + ')' });
  }
  return list;
}

function slotName(monsters, slot) {
  if (typeof slot !== 'number') { return ''; }
  const arr = Array.isArray(monsters) ? monsters : [];
  const m = arr[slot];
  if (!m) { return 'slot ' + (slot + 1); }
  const cn = (m.customName && String(m.customName).trim()) ? String(m.customName).trim() : '';
  const sm = (m.selectedMonster && String(m.selectedMonster).trim()) ? String(m.selectedMonster).trim() : '';
  return (cn || sm || ('slot ' + (slot + 1)));
}

function nz(v) { return (v === null || typeof v === 'undefined') ? '' : v; }

// ---- Overview card ---------------------------------------------------------
function BriefCard(props) {
  const configured = props.configured;
  return (
    <div className={'vi-eb-card' + (configured ? ' is-set' : '')} onClick={props.onOpen} role="button" tabIndex={0}>
      <div className="vi-eb-card-head">
        <span className="vi-eb-card-icon">{props.icon}</span>
        <span className="vi-eb-card-title">{props.title}</span>
      </div>
      <div className="vi-eb-card-body">
        {configured ? props.children : <div className="vi-eb-card-empty">{props.emptyText}</div>}
      </div>
      <div className="vi-eb-card-foot">
        <span className="vi-eb-card-link">{configured ? 'View / Edit' : props.addText}</span>
      </div>
    </div>
  );
}

// ---- Event editor row ------------------------------------------------------
function EventRow(props) {
  const e = props.event;
  const slots = props.slots;
  const due = props.due;
  const t = e.trigger || {};
  return (
    <div className={'vi-eb-item vi-eb-event status-' + e.status}>
      <div className="vi-eb-item-top">
        <input className="vi-tr-input vi-eb-grow" placeholder="Event name (e.g. Guard Reinforcements)" value={nz(e.name)} onChange={function (ev) { props.onUpdate(e.id, { name: ev.target.value }); }} />
        <span className={'vi-tr-status vi-eb-kind'}>{EVENT_KIND_LABELS[e.kind] || e.kind}</span>
        <button type="button" className="vi-tr-mini vi-tr-mini-danger" title="Remove event" onClick={function () { props.onRemove(e.id); }}>{'✖'}</button>
      </div>
      <div className="vi-eb-item-row">
        <input className="vi-tr-input vi-eb-grow" placeholder="Monsters (e.g. 2 x Cult Fanatic)" value={nz(e.monsters)} onChange={function (ev) { props.onUpdate(e.id, { monsters: ev.target.value }); }} />
        <input className="vi-tr-input vi-eb-grow" placeholder="Entry / location (e.g. North stairs)" value={nz(e.entry)} onChange={function (ev) { props.onUpdate(e.id, { entry: ev.target.value }); }} />
      </div>
      <div className="vi-eb-item-row vi-eb-trigger">
        <span className="vi-eb-mini-label">Trigger</span>
        {TRIGGER_TYPES.map(function (tt) {
          return <button key={tt} type="button" className={'vi-tr-chip' + (t.type === tt ? ' is-on' : '')} onClick={function () { props.onUpdate(e.id, { trigger: { type: tt } }); }}>{TRIGGER_LABELS[tt]}</button>;
        })}
        {t.type === 'round' ? (
          <label className="vi-eb-inline">Round <input className="vi-tr-input vi-tr-input-sm" type="number" min="1" value={nz(t.round)} onChange={function (ev) { props.onUpdate(e.id, { trigger: { round: ev.target.value === '' ? null : parseInt(ev.target.value, 10) } }); }} /></label>
        ) : null}
        {t.type === 'hp' ? (
          <React.Fragment>
            <label className="vi-eb-inline">At <input className="vi-tr-input vi-tr-input-sm" type="number" min="1" max="100" value={nz(t.hpPct)} onChange={function (ev) { props.onUpdate(e.id, { trigger: { hpPct: ev.target.value === '' ? null : parseInt(ev.target.value, 10) } }); }} /> % HP</label>
            <select className="vi-tr-input" value={(t.slot === null || typeof t.slot === 'undefined') ? '' : String(t.slot)} onChange={function (ev) { props.onUpdate(e.id, { trigger: { slot: ev.target.value === '' ? null : parseInt(ev.target.value, 10) } }); }}>
              <option value="">Monster...</option>
              {slots.map(function (s) { return <option key={s.index} value={String(s.index)}>{s.label}</option>; })}
            </select>
          </React.Fragment>
        ) : null}
        {(t.type === 'group-defeated' || t.type === 'manual') ? (
          <input className="vi-tr-input vi-eb-grow" placeholder={t.type === 'group-defeated' ? 'Which group (e.g. initial enemy group)' : 'When to activate (DM note)'} value={nz(t.note)} onChange={function (ev) { props.onUpdate(e.id, { trigger: { note: ev.target.value } }); }} />
        ) : null}
      </div>
      <div className="vi-eb-item-row vi-eb-status-row">
        {due && e.status === 'pending' ? <span className="vi-eb-due">Trigger reached</span> : null}
        {EVENT_STATUSES.map(function (st) {
          return <button key={st} type="button" className={'vi-tr-chip' + (e.status === st ? ' is-on' : '')} onClick={function () { props.onStatus(e.id, st); }}>{st.charAt(0).toUpperCase() + st.slice(1)}</button>;
        })}
      </div>
    </div>
  );
}

// ---- Rule editor row -------------------------------------------------------
function RuleRow(props) {
  const r = props.rule;
  const slots = props.slots;
  return (
    <div className={'vi-eb-item vi-eb-rule kind-' + r.kind}>
      <div className="vi-eb-item-top">
        <input className="vi-tr-input vi-eb-grow" placeholder={r.kind === 'variant' ? 'Variant (e.g. Enraged)' : (r.kind === 'hazard' ? 'Hazard (e.g. Burning floor)' : 'Rule title')} value={nz(r.title)} onChange={function (ev) { props.onUpdate(r.id, { title: ev.target.value }); }} />
        <span className="vi-tr-status vi-eb-kind">{RULE_KIND_LABELS[r.kind] || r.kind}</span>
        {r.kind === 'hazard' ? (
          <button type="button" className={'vi-tr-chip' + (r.active ? ' is-on' : '')} onClick={function () { props.onToggle(r.id); }}>{r.active ? 'Active' : 'Inactive'}</button>
        ) : null}
        <button type="button" className="vi-tr-mini vi-tr-mini-danger" title="Remove rule" onClick={function () { props.onRemove(r.id); }}>{'✖'}</button>
      </div>
      {r.kind === 'variant' ? (
        <div className="vi-eb-item-row">
          <span className="vi-eb-mini-label">Applies to</span>
          <select className="vi-tr-input" value={(r.slot === null || typeof r.slot === 'undefined') ? '' : String(r.slot)} onChange={function (ev) { props.onUpdate(r.id, { slot: ev.target.value === '' ? null : parseInt(ev.target.value, 10) }); }}>
            <option value="">Monster slot...</option>
            {slots.map(function (s) { return <option key={s.index} value={String(s.index)}>{s.label}</option>; })}
          </select>
        </div>
      ) : null}
      <div className="vi-eb-item-row">
        <input className="vi-tr-input vi-eb-grow" placeholder="Detail (short, battle-relevant)" value={nz(r.detail)} onChange={function (ev) { props.onUpdate(r.id, { detail: ev.target.value }); }} />
      </div>
    </div>
  );
}

function EncounterBriefView() {
  const ctx = useContext(CombatContext);
  const state = ctx.state;
  const [sub, setSub] = useState('overview');

  const store = state.encounterStore;
  const activeIndex = (store && typeof store.activeIndex === 'number') ? store.activeIndex : 0;
  const enc = (store && Array.isArray(store.encounters)) ? (store[familyArrayKey(activeFamilyOf(store))] || store.encounters)[activeIndex] : null;
  const encName = (enc && enc.meta && enc.meta.name) ? enc.meta.name : ('Encounter ' + (activeIndex + 1));
  const brief = (enc && enc.encounterBrief) ? enc.encounterBrief : defaultBrief();
  const round = (typeof state.currentRound === 'number') ? state.currentRound : 1;
  const monsters = state.monsters;
  const slots = populatedSlots(monsters, state.activeMonsterCount);
  const sum = briefSummary(brief);
  const configured = briefIsConfigured(brief);

  const goal = brief.goal || {};
  const setup = brief.setup || {};
  const events = Array.isArray(brief.events) ? brief.events : [];
  const rules = Array.isArray(brief.rules) ? brief.rules : [];
  const nextEv = nextEvent(brief, round);

  function head(title) {
    return (
      <div className="vi-eb-detail-head">
        <button type="button" className="vi-tr-btn" onClick={function () { setSub('overview'); }}>{'←'} Overview</button>
        <div className="vi-eb-detail-titles">
          <h2 className="vi-tr-page-title">{title}</h2>
          <p className="vi-tr-page-sub">{encName}</p>
        </div>
      </div>
    );
  }

  // ---------------- OVERVIEW ----------------
  if (sub === 'overview') {
    const activeHazards = rules.filter(function (r) { return r.kind === 'hazard' && r.active; });
    const activePhases = events.filter(function (e) { return e.status === 'active'; });
    const showRun = configured;
    return (
      <div className="vi-view vi-eb-view" id="view-encounter-brief">
        <div className="vi-tr-page-head">
          <h1 className="vi-tr-page-title">{'📜'} Encounter Brief</h1>
          <p className="vi-tr-page-sub">What makes this battle different {'·'} {encName}</p>
        </div>

        {showRun ? (
          <div className="vi-eb-runstrip">
            {(goal.objective && goal.objective.trim()) ? (
              <div className="vi-eb-run-item"><span className="vi-eb-run-label">Objective</span><span className="vi-eb-run-val">{goal.objective}{goal.resolution && goal.resolution !== 'open' ? <span className={'vi-eb-res res-' + goal.resolution}>{RESOLUTION_LABELS[goal.resolution]}</span> : null}</span></div>
            ) : null}
            {nextEv ? (
              <div className="vi-eb-run-item"><span className="vi-eb-run-label">Next event</span><span className="vi-eb-run-val">{triggerLabel(nextEv)}{nextEv.name ? (' \u2014 ' + nextEv.name) : ''}{evaluateEventDue(nextEv, { round: round, monsters: monsters }) && nextEv.status === 'pending' ? <span className="vi-eb-due">reached</span> : null}</span></div>
            ) : null}
            {activePhases.length ? (
              <div className="vi-eb-run-item"><span className="vi-eb-run-label">Active</span><span className="vi-eb-run-val">{activePhases.map(function (e) { return e.name || EVENT_KIND_LABELS[e.kind]; }).join(', ')}</span></div>
            ) : null}
            {activeHazards.length ? (
              <div className="vi-eb-run-item"><span className="vi-eb-run-label">Hazard</span><span className="vi-eb-run-val">{activeHazards.map(function (r) { return r.title || 'Hazard'; }).join(', ')} {'·'} active</span></div>
            ) : null}
          </div>
        ) : null}

        <div className="vi-eb-grid">
          <BriefCard icon={'🎯'} title="Encounter Goal" configured={sum.goalSet || (goal.resolution && goal.resolution !== 'open')} emptyText="No goal set" addText="+ Add Goal" onOpen={function () { setSub('goal'); }}>
            {(goal.objective && goal.objective.trim()) ? <div className="vi-eb-line"><strong>Objective:</strong> {goal.objective}</div> : null}
            {(goal.victory && goal.victory.trim()) ? <div className="vi-eb-line"><strong>Victory:</strong> {goal.victory}</div> : null}
            {(goal.failure && goal.failure.trim()) ? <div className="vi-eb-line"><strong>Failure:</strong> {goal.failure}</div> : null}
            {(goal.resolution && goal.resolution !== 'open') ? <div className="vi-eb-line"><span className={'vi-eb-res res-' + goal.resolution}>{RESOLUTION_LABELS[goal.resolution]}</span></div> : null}
          </BriefCard>

          <BriefCard icon={'⚔️'} title="Battle Setup" configured={sum.setupCount > 0} emptyText="No setup configured" addText="+ Add Setup" onOpen={function () { setSub('setup'); }}>
            {(setup.trigger && setup.trigger.trim()) ? <div className="vi-eb-line"><strong>Trigger:</strong> {setup.trigger}</div> : null}
            {(setup.entry && setup.entry.trim()) ? <div className="vi-eb-line"><strong>Entry:</strong> {setup.entry}</div> : null}
            {(Array.isArray(setup.lines) ? setup.lines : []).slice(0, 3).map(function (l) { return (l.text && l.text.trim()) ? <div className="vi-eb-line" key={l.id}>{'•'} {l.text}</div> : null; })}
            {(Array.isArray(setup.lines) && setup.lines.length > 3) ? <div className="vi-eb-line vi-eb-more">+{setup.lines.length - 3} more</div> : null}
          </BriefCard>

          <BriefCard icon={'🌊'} title="Encounter Events" configured={sum.eventCount > 0} emptyText="No events configured" addText="+ Add Events" onOpen={function () { setSub('events'); }}>
            <div className="vi-eb-line"><strong>{sum.eventCount}</strong> configured{sum.eventsResolved ? (' · ' + sum.eventsResolved + ' resolved') : ''}</div>
            {nextEv ? <div className="vi-eb-line">Next: {triggerLabel(nextEv)}{nextEv.name ? (' \u2014 ' + nextEv.name) : ''}</div> : null}
            {sum.eventsActive ? <div className="vi-eb-line">{sum.eventsActive} active now</div> : null}
          </BriefCard>

          <BriefCard icon={'⚠️'} title="Special Rules" configured={sum.ruleCount > 0} emptyText="No special rules" addText="+ Add Rules" onOpen={function () { setSub('rules'); }}>
            <div className="vi-eb-line"><strong>{sum.ruleCount}</strong> configured</div>
            {sum.hazardsActive ? <div className="vi-eb-line">{sum.hazardsActive} hazard{sum.hazardsActive === 1 ? '' : 's'} active</div> : null}
            {rules.filter(function (r) { return r.kind === 'variant' && typeof r.slot === 'number'; }).slice(0, 2).map(function (r) { return <div className="vi-eb-line" key={r.id}>{slotName(monsters, r.slot)}{r.title ? (' \u2014 ' + r.title) : ''}</div>; })}
          </BriefCard>
        </div>

        {configured ? (
          <div className="vi-eb-overview-foot">
            <button type="button" className="vi-tr-btn vi-tr-btn-danger" onClick={function () { if (window.confirm('Clear the entire Encounter Brief for ' + encName + '? This cannot be undone.')) { ctx.clearEncounterBrief(); } }}>Clear Brief</button>
          </div>
        ) : null}
      </div>
    );
  }

  // ---------------- GOAL ----------------
  if (sub === 'goal') {
    return (
      <div className="vi-view vi-eb-view" id="view-encounter-brief">
        {head('Encounter Goal')}
        <div className="vi-tr-panel">
          <label className="vi-eb-field-label">Objective</label>
          <input className="vi-tr-input vi-eb-full" placeholder="e.g. Protect Captain Vale" value={nz(goal.objective)} onChange={function (e) { ctx.updateBriefGoal({ objective: e.target.value }); }} />
          <label className="vi-eb-field-label">Victory Condition</label>
          <input className="vi-tr-input vi-eb-full" placeholder="e.g. Captain survives 5 rounds" value={nz(goal.victory)} onChange={function (e) { ctx.updateBriefGoal({ victory: e.target.value }); }} />
          <label className="vi-eb-field-label">Failure Condition</label>
          <input className="vi-tr-input vi-eb-full" placeholder="e.g. Captain is defeated" value={nz(goal.failure)} onChange={function (e) { ctx.updateBriefGoal({ failure: e.target.value }); }} />
          <label className="vi-eb-field-label">Other End Condition</label>
          <input className="vi-tr-input vi-eb-full" placeholder="e.g. Ritual completes at end of Round 6" value={nz(goal.endCondition)} onChange={function (e) { ctx.updateBriefGoal({ endCondition: e.target.value }); }} />
        </div>
        <div className="vi-tr-panel">
          <div className="vi-tr-panel-title">Resolution</div>
          <p className="vi-eb-help">These require DM judgement. Mark the outcome manually when the battle resolves.</p>
          <div className="vi-eb-chiprow">
            {RESOLUTIONS.map(function (r) {
              return <button key={r} type="button" className={'vi-tr-chip' + (goal.resolution === r ? ' is-on' : '')} onClick={function () { ctx.updateBriefGoal({ resolution: r }); }}>{RESOLUTION_LABELS[r]}</button>;
            })}
          </div>
        </div>
      </div>
    );
  }

  // ---------------- BATTLE SETUP ----------------
  if (sub === 'setup') {
    const lines = Array.isArray(setup.lines) ? setup.lines : [];
    const zones = Array.isArray(setup.zones) ? setup.zones : [];
    function addZone() { ctx.updateBriefSetup({ zones: zones.concat([newZone('')]) }); }
    function updateZone(id, patch) { ctx.updateBriefSetup({ zones: zones.map(function (z) { return z.id === id ? Object.assign({}, z, patch) : z; }) }); }
    function removeZone(id) { ctx.updateBriefSetup({ zones: zones.filter(function (z) { return z.id !== id; }) }); }
    return (
      <div className="vi-view vi-eb-view" id="view-encounter-brief">
        {head('Battle Setup')}
        <div className="vi-tr-panel">
          <label className="vi-eb-field-label">Battle Trigger</label>
          <input className="vi-tr-input vi-eb-full" placeholder="e.g. Alarm sounds; combat starts surprised" value={nz(setup.trigger)} onChange={function (e) { ctx.updateBriefSetup({ trigger: e.target.value }); }} />
          <label className="vi-eb-field-label">Party Entry Point</label>
          <input className="vi-tr-input vi-eb-full" placeholder="e.g. South door, single file" value={nz(setup.entry)} onChange={function (e) { ctx.updateBriefSetup({ entry: e.target.value }); }} />
        </div>
        <div className="vi-tr-panel">
          <div className="vi-tr-panel-title">Starting State</div>
          <p className="vi-eb-help">Short battle-specific notes: enemy hidden, boss enraged, fire active, exits blocked, civilians present, positions.</p>
          {lines.length ? lines.map(function (l) {
            return (
              <div className="vi-eb-item-row" key={l.id}>
                <input className="vi-tr-input vi-eb-grow" placeholder="Setup note" value={nz(l.text)} onChange={function (e) { ctx.updateBriefSetupLine(l.id, e.target.value); }} />
                <button type="button" className="vi-tr-mini vi-tr-mini-danger" title="Remove" onClick={function () { ctx.removeBriefSetupLine(l.id); }}>{'✖'}</button>
              </div>
            );
          }) : <div className="vi-tr-empty">No setup notes yet.</div>}
          <div className="vi-eb-chiprow"><button type="button" className="vi-tr-btn" onClick={function () { ctx.addBriefSetupLine(''); }}>+ Add setup note</button></div>
        </div>
        <div className="vi-tr-panel">
          <div className="vi-tr-panel-title">Battlefield Zones</div>
          <p className="vi-eb-help">Named tactical areas (North Balcony, Ritual Circle, Burning Floor). Not a map: concise labels you assign combatants to from Selection, and that events and hazards can reference.</p>
          {zones.length ? zones.map(function (z) {
            return (
              <div className="vi-eb-item" key={z.id}>
                <div className="vi-eb-item-top">
                  <input className="vi-tr-input vi-eb-grow" placeholder="Zone name" value={nz(z.name)} onChange={function (e) { updateZone(z.id, { name: e.target.value }); }} />
                  <button type="button" className={'vi-tr-chip' + (z.entrance ? ' is-on' : '')} onClick={function () { updateZone(z.id, { entrance: !z.entrance }); }}>Entrance</button>
                  <button type="button" className="vi-tr-mini vi-tr-mini-danger" title="Remove zone" onClick={function () { removeZone(z.id); }}>{'\u2716'}</button>
                </div>
                <div className="vi-eb-item-row">
                  <input className="vi-tr-input vi-eb-grow" placeholder="Description / tags" value={nz(z.description)} onChange={function (e) { updateZone(z.id, { description: e.target.value }); }} />
                  <input className="vi-tr-input vi-eb-grow" placeholder="Hazard / effect link (optional)" value={nz(z.hazard)} onChange={function (e) { updateZone(z.id, { hazard: e.target.value }); }} />
                </div>
              </div>
            );
          }) : <div className="vi-tr-empty">No zones yet.</div>}
          <div className="vi-eb-chiprow"><button type="button" className="vi-tr-btn" onClick={addZone}>+ Add zone</button></div>
        </div>
      </div>
    );
  }

  // ---------------- EVENTS ----------------
  if (sub === 'events') {
    return (
      <div className="vi-view vi-eb-view" id="view-encounter-brief">
        {head('Encounter Events')}
        <div className="vi-tr-panel">
          <p className="vi-eb-help">Waves, reinforcements and phase changes. Round and HP triggers watch the live combat state and flag when reached; you activate and resolve them manually.</p>
          <div className="vi-eb-chiprow">
            <span className="vi-eb-mini-label">Add</span>
            {EVENT_KINDS.map(function (k) { return <button key={k} type="button" className="vi-tr-chip" onClick={function () { ctx.addBriefEvent(k); }}>{'+ ' + EVENT_KIND_LABELS[k]}</button>; })}
          </div>
        </div>
        {events.length ? events.map(function (e) {
          return <EventRow key={e.id} event={e} slots={slots} due={evaluateEventDue(e, { round: round, monsters: monsters })} onUpdate={ctx.updateBriefEvent} onStatus={ctx.setBriefEventStatus} onRemove={ctx.removeBriefEvent} />;
        }) : <div className="vi-tr-empty">No events yet. Add a wave, reinforcement or phase change above.</div>}
      </div>
    );
  }

  // ---------------- SPECIAL RULES ----------------
  if (sub === 'rules') {
    return (
      <div className="vi-view vi-eb-view" id="view-encounter-brief">
        {head('Special Rules')}
        <div className="vi-tr-panel">
          <p className="vi-eb-help">Hazards and environment, custom battle rules, and encounter-specific monster variants. Variants attach to a monster slot and never change the canonical monster.</p>
          <div className="vi-eb-chiprow">
            <span className="vi-eb-mini-label">Add</span>
            {RULE_KINDS.map(function (k) { return <button key={k} type="button" className="vi-tr-chip" onClick={function () { ctx.addBriefRule(k); }}>{'+ ' + RULE_KIND_LABELS[k]}</button>; })}
          </div>
        </div>
        {rules.length ? rules.map(function (r) {
          return <RuleRow key={r.id} rule={r} slots={slots} onUpdate={ctx.updateBriefRule} onToggle={ctx.toggleBriefRuleActive} onRemove={ctx.removeBriefRule} />;
        }) : <div className="vi-tr-empty">No special rules yet. Add a hazard, custom rule or monster variant above.</div>}
      </div>
    );
  }

  return null;
}

export default EncounterBriefView;
